import {FileManagerFile} from "../FileManagerFile";
import Product from "@/product/interfaces/Product";
import {Flowchart} from "@/core/interfaces/flowchart/flowchart";
import {CustomerSummary} from "@/customer/interfaces/Customer";

export enum QuestionType {
    SINGLE_CHOICE = "single_choice",
    MULTIPLE_CHOICE = "multiple_choice",
    YES_NO = "yes_no",
    TEXT_INPUT = 'text_input',
    CHECK_INPUT = 'check_input',
    DATE = 'date',
    YES_NO_WITH_PRODUCTS = 'yes_no_with_products'
}

export interface SurveyIndex {
    /** Survey id. */
    id: number;
    /** Survey slug. */
    slug: string;
    /** Survey name. */
    name: string;
    /** Survey emoji. */
    emoji?: string;
    /** Survey theme. */
    theme?: string;
    /** Survey description. */
    description: string;
    /** Survey file image. */
    file: FileManagerFile | null;
}

export interface Survey extends SurveyIndex {
    /** Survey sections. */
    sections?: SurveySection[];
    /** Survey creation date. */
    created_at: string;
    /** Survey last update date. */
    updated_at: string;
    /** Survey consent text. */
    consent: string | null;
    /** The flowchart related to the survey */
    flowchart?: Flowchart;
    /** Survey invoicing text. */
    invoicing: string;
}

export interface SurveySection {
    /** Survey section id. */
    id: number;
    /** Survey section title id. */
    title?: string;
    /** Survey section questions. */
    questions?: SurveyQuestion[];
    /** Survey section creation date. */
    created_at: string;
    /** Survey section last update date. */
    updated_at: string;
}

export interface BaseSurveyQuestion {
    /** Survey question id. */
    id: number;
    /** Survey question title. */
    title: string;
    /** Survey question description. */
    description: string;
    /** Survey question type. */
    type: SurveyQuestionType;
    /** The image associated with the question */
    image: null | string;
    /** List of all the possible answers of the survey question. */
    answers?: unknown
    /** Previous answer to the question registered in the current instance (null if there is none). */
    user_answer: {
        value: (string | number | boolean | undefined)[] | Record<string, unknown>;
    } | null;
    /** Is optional ? */
    optional: boolean;
    /** Survey question creation date. */
    created_at: string;
    /** Survey question last update date. */
    updated_at: string;
}

export interface YesNoSurveyQuestion extends BaseSurveyQuestion {
    type: SurveyQuestionType<QuestionType.YES_NO>;
    answers: [
        {
            value: true;
            label: string;
        },
        {
            value: false;
            label: string;
        }
    ],
}

export interface TextInputSurveyQuestion extends BaseSurveyQuestion {
    type: SurveyQuestionType<QuestionType.TEXT_INPUT>
    answers: [];
}

export interface ChoiceBasedSurveyQuestion extends BaseSurveyQuestion {
    answers: (BaseSurveyQuestionAnswer & { color?: string; image?: string })[];
    user_answer: { value: (number | string | boolean)[] } | null;
}

export interface MultipleChoiceSurveyQuestion extends ChoiceBasedSurveyQuestion {
    type: SurveyQuestionType<QuestionType.MULTIPLE_CHOICE>
}

export interface SingleChoiceSurveyQuestion extends ChoiceBasedSurveyQuestion {
    type: SurveyQuestionType<QuestionType.SINGLE_CHOICE>
}

export interface DateSurveyQuestion extends BaseSurveyQuestion {
    type: SurveyQuestionType<QuestionType.DATE>
    answers: [];
}

export interface CheckInputSurveyQuestion extends BaseSurveyQuestion {
    type: SurveyQuestionType<QuestionType.CHECK_INPUT>
    answers: { label: string }[]
}

export interface YesNoWithProductsQuestion extends BaseSurveyQuestion {
    type: SurveyQuestionType<QuestionType.YES_NO_WITH_PRODUCTS>
    answers: [
        {
            value: true;
            label: string;
            products: { id: number; name: string }[];
        },
        {
            value: false;
            label: string;
        }
    ],
    user_answer: null | { value: { value: boolean[], products: number[] | null }; }
}

export type SurveyQuestion = YesNoSurveyQuestion
    | TextInputSurveyQuestion
    | MultipleChoiceSurveyQuestion
    | SingleChoiceSurveyQuestion
    | DateSurveyQuestion
    | CheckInputSurveyQuestion
    | YesNoWithProductsQuestion

export interface SurveyQuestionType<T = QuestionType> {
    /** Survey question type id. */
    id: number;
    /** Survey question type name. */
    name: string;
    /** Survey question type slug. */
    slug: T;
    /** Survey question type strategy. */
    strategy: string;
}

export interface BaseSurveyQuestionAnswer {
    value: (number | boolean);
    label: string;
}

export interface SurveyQuestionAnswer {
    /** Survey question answer value. */
    value: (number | boolean);
    /** Content of the survey question answer. */
    label: string;
    /** Color of the answer. */
    color?: string;
    /** Illustration path of the answer. */
    image?: string;
}

/** A choice answer row, as returned by the prefillers of choice based questions. */
export interface PrefilledSurveyAnswerChoice {
    /** Survey question answer id, matching the `value` of the built answer. */
    id: number;
    /** Survey question answer content. */
    content: string;
    /** Survey question answer slug. */
    slug: string | null;
}

export interface PrefilledSurveyAnswer {
    /** Survey question id. */
    question_id: number;
    /**
     * Survey answer unserialized value: answer rows for choice based questions,
     * scalars for the other types. Null when nothing could be prefilled.
     */
    answer: Array<number | boolean | string | PrefilledSurveyAnswerChoice> | null;
    /** Prefill reason. */
    reason: string | null;
}

export interface SurveyVerdict {
    /** Survey verdict title. */
    title: string;
    /** Verdict products */
    products: Product[];
    /** Survey verdict score. */
    score: number;
    /** Survey verdict slug. */
    slug: string;
    /**
     * Slug dedicated to flowchart routing, only present when the counting engine
     * implements FlowchartRoutable. May group several verdicts on a single edge.
     */
    flowchart_slug?: string;
    /**
     *  Value used to evaluate the score (for example in the case of a survey, that has the purpose to find if you are
     *  late for a vaccine injection, true would mean you need to do it, false that it's done).
     */
    verdict: boolean | object;
    /** Survey verdict description. */
    description: string;
    /** Survey verdict marketing information. */
    marketingInformation?: string;
    /** The survey belonging to the instance of the verdict. */
    survey?: SurveyIndex;
    /** Options specific to this survey verdict. */
    options?: {
        isScoreEssential: boolean;
    };
}

export interface SurveyAnswerInstance {
    /** If if the survey answer instance. */
    id: number;
    /** Consumer key of the person who is providing the answers. */
    consumer_key: string;
    /** Type of the person who is filling the answers. */
    filler_type: string;
    /** Id of the person who is filling the answers. */
    filler_id: number;
    /** Name of the filler. */
    filler?: {
        name: string;
    };
    /** The survey associated with the answer instance. */
    survey: Survey;
    /** The survey verdict of the survey answer instance. */
    verdict?: SurveyVerdict;
    /** Survey answer instance creation date. */
    created_at: string;
    /** Survey answer instance completed date. */
    completed_at?: string;

    ai_summary?: {
        summary?: string;
    };
}

export interface SurveyAnswerInstanceIndex extends SurveyAnswerInstance {
    customer?: CustomerSummary;
    name?: string;
    /** Whether the authenticated user can delete the instance, as decided by the HDS. */
    deletable?: boolean;
}
